import React, { useMemo } from "react"
import { Row, Col, Typography, Divider, Collapse, Tooltip } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { numberToCurrency } from "helpers/string"
import { colors } from "themes/variables"
import { BACKEND_CARD_FEE_TITLE, getSavedBookingTotals } from "utils/bookingCalculations"
import { isFailedPaymentStatus } from "ui/Booking/Details/utils"

const { Text } = Typography

const PreviousPriceDetail = ({ booking }) => {
  const { payment_summary = {}, payment_history = [], booking_add_ons = [] } = booking || {}
  const {
    tax_and_fee,
    adult,
    child,
    infant,
    remaining_balance,
    passenger,
    pricing_type,
    extra_adult,
    extra_child,
    extra_infant,
    extra_passenger,
    flat_rate,
    custom_fees,
    discount_amount,
    discount_code,
    subtotal_adjusted,
    original_subtotal,
    adjusted_subtotal,
    subtotal_adjustment_reason,
    adjust_price_history = [],
    ticket_lines
  } = payment_summary ?? {}
  const isSubtotalAdjusted = !!subtotal_adjusted && adjusted_subtotal != null
  // Taken out of Tax & Fees above and rendered below the Total instead.
  const { processingFee, bookingTotal, totalCharged } = getSavedBookingTotals(payment_summary)
  const { t } = useTranslation()
  const summaryDetails = useMemo(() => {
    const details = []

    const addPricingDetails = (_type, count, price, label) => {
      if (count && price) {
        details.push({ label: `${label} x ${count}`, value: price })
      }
    }

    const addPerPersonPricing = () => {
      if (pricing_type !== "charge_per_person") return
      addPricingDetails(adult, adult?.count, adult?.price, "Adult")
      addPricingDetails(child, child?.count, child?.price, "Child")
      addPricingDetails(infant, infant?.count, infant?.price, "Infant")
      addPricingDetails(passenger, passenger?.count, passenger?.price, "Passenger")
    }

    const addFlatRatePricing = () => {
      if (pricing_type !== "charge_flat_rate") return
      details.push({ label: "Flat Rate For Main Guests", value: flat_rate ?? 0 })
      addPricingDetails(extra_adult, extra_adult?.count, extra_adult?.price, "Extra Adult")
      addPricingDetails(extra_child, extra_child?.count, extra_child?.price, "Extra Child")
      addPricingDetails(extra_infant, extra_infant?.count, extra_infant?.price, "Extra Infant")
      addPricingDetails(extra_passenger, extra_passenger?.count, extra_passenger?.price, "Extra Passenger")
    }

    // A ticketed booking (QA Ticketing #57): a line per ticket type, as it was sold.
    const addTicketPricing = () =>
      (ticket_lines ?? []).forEach((line) => addPricingDetails(null, line.quantity, Number(line.total), line.title))

    addPerPersonPricing()
    addFlatRatePricing()
    addTicketPricing()

    // Negotiated subtotal (set at creation): show the guest lines, then a Subtotal row that strikes
    // the original computed subtotal and surfaces the adjusted base + reason — so the line items and
    // the total reconcile for adjusted bookings, matching the create-screen breakdown.
    if (isSubtotalAdjusted) {
      details.push({
        isSubtotalAdjusted: true,
        label: t("booking.labels.subtotal", "Subtotal"),
        originalValue: Number(original_subtotal ?? 0),
        value: Number(adjusted_subtotal),
        reason: subtotal_adjustment_reason
      })
    }

    if (discount_amount > 0) {
      const discountLabel = discount_code ? `Discount (${discount_code})` : "Discount"
      details.push({ label: discountLabel, value: -discount_amount, isDiscount: true })
    }

    const addOnsTotal = (booking_add_ons ?? []).reduce((sum, item) => sum + Number(item.total_price || 0), 0)
    if (addOnsTotal > 0) {
      details.push({
        label: t("booking.add_ons.title", { defaultValue: "Add-ons" }),
        value: addOnsTotal
      })
    }

    // Build Tax & Fees as a grouped entry with children. The payment processing fee is left out —
    // it is a transaction fee on the amount charged, shown on its own line below the Total.
    const taxAndFeeItems = (custom_fees ?? []).filter((fee) => fee.title !== BACKEND_CARD_FEE_TITLE)
    const customFeeItems = taxAndFeeItems.map((fee) => ({
      label: fee.title,
      amount: numberToCurrency(Number(fee.price))
    }))
    // The platform fee (tax_and_fee) is already represented inside custom_fees as a "Platform Fee"
    // entry. Only add it separately for legacy bookings whose custom_fees omit it — otherwise it
    // renders twice.
    const hasPlatformFee = taxAndFeeItems.some((fee) => fee.title === "Platform Fee")
    const platformFeeAmount = hasPlatformFee ? 0 : (tax_and_fee ?? 0)
    if (platformFeeAmount > 0) {
      customFeeItems.push({ label: "Platform Fee", amount: numberToCurrency(platformFeeAmount) })
    }
    const customFeeTotal = taxAndFeeItems.reduce((sum, fee) => sum + Number(fee.price), 0)
    const taxFeesTotal = customFeeTotal + platformFeeAmount
    if (taxFeesTotal > 0) {
      details.push({
        label: "Tax & Fees",
        value: taxFeesTotal,
        children: customFeeItems.length > 0 ? customFeeItems : undefined
      })
    }

    return details
  }, [
    adult, child, infant, passenger,
    tax_and_fee, flat_rate, extra_adult,
    extra_child, extra_infant, extra_passenger,
    pricing_type, custom_fees, discount_amount, discount_code,
    booking_add_ons, t,
    isSubtotalAdjusted, original_subtotal, adjusted_subtotal, subtotal_adjustment_reason, ticket_lines
  ])

  return (
    <div>
      {summaryDetails
        .filter(({ value }) => value !== undefined && value !== null && value !== "")
        .map((item) => {
          const { label, value, children, isDiscount } = item
          if (item.isSubtotalAdjusted) {
            return (
              <React.Fragment key="subtotal-adjustment">
                <Row justify="space-between" align="middle" style={{ marginTop: 8 }}>
                  <Col>
                    <Text style={{ fontWeight: 500 }}>{label}</Text>
                  </Col>
                  <Col>
                    <Text delete type="secondary" style={{ marginRight: 6 }}>
                      {numberToCurrency(item.originalValue)}
                    </Text>
                    <Text strong style={{ color: "var(--color-primary)", marginRight: 4 }}>
                      {numberToCurrency(value)}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      ({t("global.adjusted", "adjusted")})
                    </Text>
                  </Col>
                </Row>
                {item.reason && (
                  <Row style={{ marginTop: 2 }}>
                    <Col>
                      <Text type="secondary" style={{ fontSize: 12, fontStyle: "italic" }}>
                        {t("booking.labels.reason", "Reason")}: {item.reason}
                      </Text>
                    </Col>
                  </Row>
                )}
              </React.Fragment>
            )
          }
          return children?.length > 0 ? (
            <Collapse
              key={`${label}-${value}`}
              ghost
              style={{ marginTop: 8 }}
              items={[
                {
                  key: "tax-fees",
                  styles: {
                    header: { padding: 0 },
                    body: { padding: "4px 0 0 16px" }
                  },
                  label: (
                    <Row justify="space-between">
                      <Col><Text>{label}</Text></Col>
                      <Col><Text>&nbsp;&nbsp;{numberToCurrency(value)}</Text></Col>
                    </Row>
                  ),
                  children: children.map((child) => (
                    <Row key={`${child.label}-${child.amount}`} justify="space-between" style={{ marginTop: 4 }}>
                      <Col><Text type="secondary">{child.label}</Text></Col>
                      <Col><Text type="secondary">&nbsp;&nbsp;{child.amount}</Text></Col>
                    </Row>
                  ))
                }
              ]}
            />
          ) : (
            <Row key={`${label}-${value}`} justify="space-between" style={{ marginTop: 8 }}>
              <Col><Text type={isDiscount ? "danger" : undefined}>{label}</Text></Col>
              <Col><Text fw={500} type={isDiscount ? "danger" : undefined}>&nbsp;&nbsp;{numberToCurrency(value)}</Text></Col>
            </Row>
          )
        })}
      <Divider style={{ margin: "16px 0" }} />

      {/* Post-creation price changes (details-page "Adjust Booking Price"): shown after Tax & Fees
          and before the total so the line items reconcile with total_price (which already includes them). */}
      {adjust_price_history?.map((item, idx) => {
        const { price_difference, reason } = item || {}
        return (
          <Row key={(item?.id ?? idx) + "-price-change"} justify="space-between" style={{ marginTop: 8 }}>
            <Col>
              <Text>{t("booking.labels.price_change", "Price Change")}</Text>
              {reason && (
                <Tooltip title={reason} trigger={["hover", "focus"]}>
                  <InfoCircleOutlined
                    style={{ marginLeft: 4, color: "var(--color-primary)" }}
                    role="img"
                    aria-label={t("booking.labels.price_change_reason", "Price change reason")}
                    tabIndex={0}
                  />
                </Tooltip>
              )}
            </Col>
            <Col>
              <Text style={{ fontWeight: 500 }}>
                {price_difference > 0 ? "+" : ""}
                {numberToCurrency(price_difference)}
              </Text>
            </Col>
          </Row>
        )
      })}

      <Row justify="space-between" style={{ marginTop: 8 }}>
        <Col>
          <Text strong>{t("booking.labels.total")}</Text>
        </Col>
        <Col>
          <Text strong>{numberToCurrency(bookingTotal)}</Text>
        </Col>
      </Row>
      {processingFee > 0 && (
        <>
          <Row justify="space-between" style={{ marginTop: 8 }}>
            <Col>
              <Text>{t("booking.labels.card_processing_fee")}</Text>
            </Col>
            <Col>
              <Text>+{numberToCurrency(processingFee)}</Text>
            </Col>
          </Row>
          <Row justify="space-between" style={{ marginTop: 8 }}>
            <Col>
              <Text strong>{t("booking.labels.total_charged_to_card")}</Text>
            </Col>
            <Col>
              <Text strong>{numberToCurrency(totalCharged)}</Text>
            </Col>
          </Row>
        </>
      )}
      {payment_history?.map((history) => {
        const { type, method, last4, amount, id, status } = history || {}
        const isRefund = type?.toLowerCase() === "refund"
        // The balance below comes from the API and already excludes failed attempts; marking them
        // here keeps the rows above it from contradicting it.
        const isFailed = isFailedPaymentStatus(status)
        const key = id || `${type}-${method}-${last4}-${amount}`
        return (
          <Row key={key} justify="space-between" style={{ marginTop: 8 }}>
            <Col>
              <Text>
                {type || ""} ({method || ""}
                {last4 && ` **** ${last4}`})
                {isFailed && ` — ${t("booking.reservation_detail.payment_failed")}`}
              </Text>
            </Col>
            <Col>
              <Text delete={isFailed} color={isFailed || isRefund ? colors.error : colors.textPrimary}>
                {amount ? numberToCurrency(amount) : ""}
              </Text>
            </Col>
          </Row>
        )
      })}
      <Row justify="space-between" style={{ marginTop: 8 }}>
        <Col>
          <Text strong>{t("booking.labels.remaining_balance")}</Text>
        </Col>
        <Col>
          <Text strong>{numberToCurrency(remaining_balance)}</Text>
        </Col>
      </Row>
    </div>
  )
}

PreviousPriceDetail.propTypes = {
  booking: PropTypes.object.isRequired,
}

export default PreviousPriceDetail
